import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/**
 * Aranabilir dropdown (combobox) — <select> drop-in replacement.
 *
 * Props:
 *  - value: seçili option'ın id'si (null/'')
 *  - onChange(id): seçim değiştiğinde çağrılır
 *  - options: [{id, ...}]
 *  - getLabel(opt): birincil gösterim
 *  - getSubLabel(opt): ikincil gri alt satır (opsiyonel)
 *  - getRightLabel(opt): sag tarafta gosterilecek bilgi (opsiyonel) — string veya { text, color } dondurebilir
 *  - searchKeys: hangi alanlardan arama yapılacak (default: ['code','name','title'])
 *  - placeholder: boş seçim metni
 *  - disabled
 *  - emptyText: hiç sonuç yoksa
 *  - style: input style
 *  - autoFocus
 */
export default function SearchableSelect({
  value, onChange, options = [],
  getLabel, getSubLabel, getRightLabel,
  searchKeys = ['code', 'name', 'title'],
  placeholder = 'Seçin...',
  disabled = false,
  emptyText = 'Sonuç bulunamadı',
  style = {},
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [dropdownPos, setDropdownPos] = useState(null); // { top, left, width }
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Dropdown'u fixed pozisyonla, parent overflow'larından etkilenmesin
  useEffect(() => {
    if (!open) { setDropdownPos(null); return; }
    const updatePos = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const selected = useMemo(
    () => options.find(o => String(o.id) === String(value)),
    [options, value]
  );
  const selectedText = selected ? (getLabel ? getLabel(selected) : String(selected.name || selected.title || selected.code || selected.id)) : '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt =>
      searchKeys.some(key => {
        const v = opt[key];
        if (v == null) return false;
        return String(v).toLowerCase().includes(q);
      })
    );
  }, [options, query, searchKeys]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (listRef.current) {
      const el = listRef.current.querySelector('[data-highlighted="true"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const selectOption = useCallback((opt) => {
    onChange(opt ? opt.id : '');
    setOpen(false);
    setQuery('');
    setHighlighted(0);
  }, [onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlighted]) selectOption(filtered[highlighted]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  useEffect(() => { setHighlighted(0); }, [query]);

  const containerStyle = {
    position: 'relative',
    width: '100%',
    ...style.containerOverride,
  };

  const triggerStyle = {
    width: '100%',
    minHeight: 32,
    padding: '6px 26px 6px 10px',
    border: '1px solid var(--border-input)',
    borderRadius: 6,
    background: disabled ? 'var(--bg-disabled, var(--bg-secondary))' : 'var(--bg-input)',
    color: selectedText ? 'var(--text-primary)' : 'var(--text-tertiary)',
    fontSize: '0.88rem',
    fontFamily: 'Outfit, sans-serif',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    ...style,
  };

  return (
    <div ref={wrapRef} style={containerStyle}>
      {open ? (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ara: kod, ad, telefon, VD/TC..."
          autoFocus={autoFocus}
          style={{
            ...triggerStyle,
            paddingRight: 26,
            cursor: 'text',
            color: 'var(--text-primary)',
          }}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(true)}
          style={triggerStyle}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selectedText || placeholder}
          </span>
        </button>
      )}

      {/* Chevron */}
      <span style={{
        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', color: 'var(--text-tertiary)', fontSize: '0.65rem',
      }}>▼</span>

      {/* Clear button */}
      {selected && !disabled && !open && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          style={{
            position: 'absolute', right: 22, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: 'var(--text-tertiary)',
            cursor: 'pointer', fontSize: '0.75rem', padding: 2,
          }}
          title="Temizle"
        >✕</button>
      )}

      {/* Dropdown — fixed positioning ile parent overflow'larını aşar */}
      {open && dropdownPos && (
        <div ref={listRef} style={{
          position: 'fixed',
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 8,
          boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.15))',
          maxHeight: 320, overflowY: 'auto',
          zIndex: 99999,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
              {emptyText}
            </div>
          ) : filtered.slice(0, 200).map((opt, i) => {
            const isHi = i === highlighted;
            const isSel = String(opt.id) === String(value);
            return (
              <div
                key={opt.id}
                data-highlighted={isHi}
                onMouseDown={(e) => { e.preventDefault(); selectOption(opt); }}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  background: isHi ? 'var(--accent-glow, rgba(59,130,246,0.08))' : (isSel ? 'var(--bg-secondary)' : 'transparent'),
                  borderBottom: '1px solid var(--border-secondary)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: isSel ? 600 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getLabel ? getLabel(opt) : (opt.name || opt.title || opt.code || opt.id)}
                  </div>
                  {getSubLabel && (() => {
                    const sub = getSubLabel(opt);
                    return sub ? (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{sub}</div>
                    ) : null;
                  })()}
                </div>
                {getRightLabel && (() => {
                  const r = getRightLabel(opt);
                  if (!r) return null;
                  const text  = typeof r === 'string' ? r : r.text;
                  const color = typeof r === 'object' && r.color ? r.color : 'var(--text-secondary)';
                  return text ? (
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color, whiteSpace: 'nowrap', flexShrink: 0, paddingLeft: 6 }}>{text}</div>
                  ) : null;
                })()}
              </div>
            );
          })}
          {filtered.length > 200 && (
            <div style={{ padding: 8, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.75rem', fontStyle: 'italic', borderTop: '1px solid var(--border-secondary)' }}>
              İlk 200 sonuç — aramayı daraltın
            </div>
          )}
        </div>
      )}
    </div>
  );
}
